import { motion } from "framer-motion";
import { Crown, Gift, HelpCircle, Receipt, Wallet, type LucideIcon } from "lucide-react";
import type { MemberStat } from "../types";
import { formatTierLabel, isMissingTier } from "../mapVerifiedMember";

const ICON_MAP: Record<MemberStat["icon"], LucideIcon> = {
  receipt: Receipt,
  wallet: Wallet,
  crown: Crown,
  gift: Gift,
};

const ACCENT: Record<
  MemberStat["icon"],
  { bg: string; icon: string; ring: string }
> = {
  receipt: {
    bg: "bg-sky-50",
    icon: "text-sky-600",
    ring: "group-hover:ring-sky-100",
  },
  wallet: {
    bg: "bg-emerald-50",
    icon: "text-emerald-600",
    ring: "group-hover:ring-emerald-100",
  },
  crown: {
    bg: "bg-amber-50",
    icon: "text-amber-600",
    ring: "group-hover:ring-amber-100",
  },
  gift: {
    bg: "bg-rose-50",
    icon: "text-rose-600",
    ring: "group-hover:ring-rose-100",
  },
};

interface StatCardProps {
  stat: MemberStat;
  index?: number;
}

/** Lifetime Spend and similar money stats: numbers only, no currency symbol. */
function displayStatValue(stat: MemberStat) {
  if (stat.id === "tier") {
    if (isMissingTier(stat.value)) {
      return (
        <span
          className="inline-flex items-center text-slate-400"
          title="Tier not available"
          aria-label="Tier not available"
        >
          <HelpCircle className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden />
        </span>
      );
    }
    return formatTierLabel(stat.value);
  }
  if (stat.id !== "spent") return stat.value;
  return String(stat.value).replace(/[^\d,.-]/g, "").trim() || "0";
}

export function StatCard({ stat, index = 0 }: StatCardProps) {
  const Icon = ICON_MAP[stat.icon];
  const accent = ACCENT[stat.icon];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.35 }}
      className={`group rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-4 ring-transparent transition duration-300 hover:-translate-y-1 hover:shadow-lg ${accent.ring}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
            {stat.label}
          </p>
          <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {displayStatValue(stat)}
          </div>
          <p className="mt-1 text-sm text-slate-500">{stat.subtitle}</p>
        </div>
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${accent.bg}`}
        >
          <Icon className={`h-5 w-5 ${accent.icon}`} />
        </div>
      </div>
    </motion.div>
  );
}
